import { LocalStorageKeys } from '@constants/enums';
import GlobalLoader from '@efex-components/common/GlobalLoader/GlobalLoader';
import { Button } from '@efexteam/components';
import { Box, Grid2, Stack, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { bck_login } from '../assets';
import ErrorRetry from '../components/common/ErrorRetry';
import leng from '../constants/leng';
import useImmediateApiCall from '../hooks/useImmediateApiCall';
import { selectSelectedLanguage } from '../pages/store/language/selectors';
import authSvc from '../services/auth/authSvc';
import { aesEncryptCBC } from '../utils/crypt';
import LogoController from './login/components/LogoController/LogoController';
import useWhiteLabelService from './login/hooks/useWhiteLabel.service';
import PasswordInput from '../components/password/PasswordInput';
import { isLegacyAuth } from '../services/auth/authUtils';
import { authenticate } from './login/auth';
import { ROUTES } from '../configs/routes';
import {
  confirmPasswordOnlySchema,
  PASSWORD_SCHEMA,
} from '../schemas/auth.schemas';

const ActiveUserEnterprise = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { whiteLabelState } = useWhiteLabelService();
  const selectedLanguage = useSelector(selectSelectedLanguage);
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(confirmPasswordOnlySchema),
    mode: 'onChange',
  });

  const { data, loading, error, retry } = useImmediateApiCall(() =>
    authSvc.validateInvitationUser({
      token: searchParams.get('tk') || '',
    })
  );

  const clearStorageKeepingOnboarding = () => {
    const onboardingData = localStorage.getItem(
      LocalStorageKeys.ONBOARDING_PROCESS_DATA
    );
    localStorage.clear();
    if (onboardingData) {
      localStorage.setItem(
        LocalStorageKeys.ONBOARDING_PROCESS_DATA,
        onboardingData
      );
    }
  };

  const handleLegacyAuthFlow = async () => {
    const response = await authSvc.userValidate();
    clearStorageKeepingOnboarding();

    if (response.isActive) {
      dispatch(authenticate(response));
      navigate(ROUTES.DASHBOARD.ROOT);
    } else {
      navigate(ROUTES.SYSTEM.UNDER_REVISION);
    }
  };

  const handleCognitoAuthFlow = () => {
    clearStorageKeepingOnboarding();
    navigate(ROUTES.AUTH.LOGIN);
  };

  const onSubmit = async (values: any) => {
    await authSvc.chosePassword({
      user: data,
      password: aesEncryptCBC(values.password),
    });

    if (isLegacyAuth()) {
      await handleLegacyAuthFlow();
    } else {
      handleCognitoAuthFlow();
    }
  };

  if (loading && !data) {
    return <GlobalLoader />;
  }

  if (error) {
    return <ErrorRetry onRetry={retry} message="Error" centered />;
  }

  return (
    <>
      <Box className="flex flex-row items-center justify-between p-3">
        <LogoController
          isWhiteLabel={!!whiteLabelState?.organizationID}
          efexLogoProps={{ className: 'max-w-[150px] ml-4 mt-2' }}
          whiteLabelLogo={whiteLabelState.organizationLogoURL}
        />
      </Box>
      <Grid2 className="h-[calc(100vh-100px)]" container>
        <Grid2
          size={{ xs: 12, md: 6 }}
          className="flex justify-center items-center"
        >
          <Stack className="p-5 w-full max-w-[550px]">
            <Typography variant="h4">Set Password</Typography>
            <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
              <PasswordInput
                control={control}
                setValue={setValue}
                name="password"
                confirmFieldName="confirmPassword"
                error={!!errors.password}
                errorMessage={errors.password?.message as string}
                confirmError={!!errors.confirmPassword}
                confirmErrorMessage={errors.confirmPassword?.message as string}
                validationSchema={PASSWORD_SCHEMA}
                enforceValidation
              />
              <Box className="flex justify-end mt-4">
                <Button appearance="primary" type="submit">
                  {leng[selectedLanguage.language].send}
                </Button>
              </Box>
            </form>
          </Stack>
        </Grid2>
        <Grid2
          size={{ xs: 12, md: 6 }}
          className="hidden md:flex justify-center items-center bg-[#EBF3FA]"
        >
          <img src={bck_login} alt="bck_login" className="w-full h-auto" />
        </Grid2>
      </Grid2>
    </>
  );
};

export default ActiveUserEnterprise;
